import React, { useEffect, useRef, useState } from "react";
import {
  cancelLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import BaseButton from "Components/Base/BaseButton";
import {
  ConditionOptions,
  ProformaKey,
  ProformaLabel,
} from "Components/constants/ProformaInvoice";
import {
  checkStatusCodeSuccess,
  currentDate,
  errorHandler,
  NormalDate,
  notFound,
  rupeeSymbol,
  TaxType,
} from "Components/constants/Common";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as yup from "yup";
import { validationMessages } from "Components/constants/validation";
import FormFields from "./FormFields";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ColDef } from "ag-grid-community";
import {
  addProformaApi,
  editProformaAPi,
  viewProformaApi,
  workOrderDataApi,
  workOrderProformaApi,
} from "Api/ProformaInvoice";
import { getWorkOrderList } from "Api/RepairOrderOpen";
import { hsnDropdownApi, listOfQuotationApi } from "Api/Quotation";
import Loader from "Components/Base/BaseLoader";
import { QuotationLabel, QuotationKey } from "Components/constants/Quotation";

const validationSchema = yup.object({
  workOrderNo: yup
    .string()
    .required(validationMessages.required(ProformaLabel.WorkOrderNo)),
  vendorName: yup
    .string()
    .required(validationMessages.required(ProformaLabel.VendorName)),
  vendorId: yup
    .string()
    .required(validationMessages.required(ProformaLabel.VendorID)),
  quoteNo: yup
    .string()
    .required(validationMessages.required(ProformaLabel.QuoteNo)),
  proformaInvoiceDT: yup
    .string()
    .required(validationMessages.required(ProformaLabel.ProformaInvoiceDT)),
  taxableValue: yup
    .string()
    .required(validationMessages.required(ProformaLabel.TaxableValue)),
  gst: yup.string().required(validationMessages.required(ProformaLabel.GST)),
  warranty: yup
    .string()
    .required(validationMessages.required(ProformaLabel.Warranty)),
  remarks: yup
    .string()
    .required(validationMessages.required(ProformaLabel.Remarks)),
  shipTo: yup
    .string()
    .required(validationMessages.required(ProformaLabel.ShipTo)),
  billTo: yup
    .string()
    .required(validationMessages.required(ProformaLabel.BillTo)),
  deliveryNote: yup
    .string()
    .required(validationMessages.required(ProformaLabel.DeliveryNote)),
  deliveryDate: yup
    .string()
    .required(validationMessages.required(ProformaLabel.DeliveryDate)),
  termsOfPayment: yup
    .string()
    .required(validationMessages.required(ProformaLabel.TermsOfPayment)),
  referenceNoDate: yup
    .string()
    .required(validationMessages.required(ProformaLabel.ReferenceNoDate)),
  vehicleRegNo: yup
    .string()
    .required(validationMessages.required(ProformaLabel.VehicleRegNo)),
  serialNo: yup
    .string()
    .required(validationMessages.required(ProformaLabel.SerialNo)),
  engineNo: yup
    .string()
    .required(validationMessages.required(ProformaLabel.EngineNo)),
  buyerOrderNo: yup
    .string()
    .required(validationMessages.required(ProformaLabel.BuyerOrderNo)),
  buyerDate: yup
    .string()
    .required(validationMessages.required(ProformaLabel.BuyerDate)),
  dispatchedThrough: yup
    .string()
    .required(validationMessages.required(ProformaLabel.DispatchedThrough)),
  destination: yup
    .string()
    .required(validationMessages.required(ProformaLabel.Destination)),
  motorVehicleNo: yup
    .string()
    .required(validationMessages.required(ProformaLabel.MotorVehicleNo)),
  deliveryTerms: yup
    .string()
    .required(validationMessages.required(ProformaLabel.DeliveryTerms)),
});

const ProformaInvoiceForm = () => {
  const [proformaInvoice, setProformaInvoice] = useState<any>(null);
  const [hsnSacOption, setHsnSacOption] = useState<Array<any>>([]);
  const [selectedProducts, setSelectedProducts] = useState<Array<any>>([]);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [work, setWork] = useState<boolean>(false);
  const navigate = useNavigate();
  const [workOrderOption, setWorkOrderOption] = useState<Array<any>>([]);
  const [workOrderData, setWorkOrderData] = useState<any>(null);
  const [quotationNumber, setQuotationNumber] = useState<any>(null);
  const [quotationOption, setQuotationOption] = useState<Array<any>>([]);
  const gridRef = useRef<AgGridReact<any>>(null);
  const { proformaId } = useParams();

  const fetchWorkOrder = () => {
    setLoader(true);
    const apiCall = proformaId ? getWorkOrderList : workOrderProformaApi;
    apiCall()
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setWorkOrderOption(
            res?.data?.map((item: any) => ({
              value: item?.id,
              label: item?.work_order_number,
              id: item?.id,
            }))
          );
        } else {
          setWorkOrderOption([]);
        }
      })
      .catch((err) => {
        setWorkOrderOption([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchWorkOrderData = (id: number) => {
    setLoader(true);
    workOrderDataApi(id)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data;
          setWorkOrderData({
            quoteNo: data?.quotation_id ?? null,
            vendorName: data?.customer_name ?? "",
            vendorId: data?.customer_id ?? "",
            referenceNoDate: NormalDate(data?.quotation_date) ?? null,
            buyerOrderNo: data?.work_order_number ?? "",
            buyerDate: NormalDate(data?.work_order_date) ?? "",
            taxableValue: data?.work_order_exl_tax_amount ?? "",
            gst: data?.gst_amount ?? "",
            taxAmount: data?.work_order_incl_tax_amount ?? "",
            shipTo: data?.quotation?.shipping_address ?? "",
            billTo: data?.quotation?.billing_address ?? "",
            discount: data?.quotation?.total_discount ?? "",
            ...(!work && {
              warranty: data?.warranty ?? "",
              termsOfPayment: data?.quotation?.terms_of_payment ?? "",
              vehicleRegNo: data?.quotation?.vehicle_no ?? "",
              serialNo: data?.quotation?.chassis_no ?? "",
              engineNo: data?.quotation?.engine_no ?? "",
              destination: data?.quotation?.destination ?? "",
              deliveryTerms: data?.quotation?.terms_of_delivery ?? "",
            }),
          });
          setQuotationNumber(data?.quotation?.quotation_number);
          setSelectedProducts(
            data?.workOrderItem?.map((item: any) => {
              return {
                ...item,
                ...(item?.product_id && {
                  product_name: item?.product?.product_code,
                  isService: false,
                  [ProformaKey.Per]: item?.product?.productUom?.uom || "",
                }),
                ...(item?.service_id && {
                  service_name: item?.service?.service_name,
                  isService: true,
                }),
                gst: TaxType?.find((tax) => tax?.value === item?.gst)?.label,
              };
            })
          );
        } else {
          setWorkOrderData([]);
        }
      })
      .catch((err) => {
        setWorkOrderData([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchProformaInvoice = async () => {
    setLoader(true);
    viewProformaApi(Number(proformaId))
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setProformaInvoice(res?.data);
        }
      })
      .catch(() => {
        setProformaInvoice(null);
      })
      .finally(() => setLoader(false));
  };

  const fetchHsnSac = () => {
    setLoader(true);
    hsnDropdownApi()
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setHsnSacOption(
            res?.data?.map((item: any) => ({
              value: item?.id,
              label: item?.code,
              type: item?.type,
              id: item?.id,
            }))
          );
        } else {
          setHsnSacOption([]);
        }
      })
      .catch((err) => {
        setHsnSacOption([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const fetchQuotation = () => {
    setLoader(true);
    const payload = {};
    listOfQuotationApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setQuotationOption(
            res?.data?.quotationList?.map((item: any) => ({
              value: item?.id,
              label: item?.quotation_number,
              id: item?.id,
            }))
          );
        } else {
          setQuotationOption([]);
        }
      })
      .catch((err) => {
        setQuotationOption([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    Promise.all([fetchHsnSac(), fetchWorkOrder(), fetchQuotation()]);
  }, []);

  useEffect(() => {
    if (proformaId) {
      fetchProformaInvoice();
    }
  }, []);

  useEffect(() => {
    if (workOrderData) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        ...workOrderData,
      }));
    } else {
      formik.setValues((prevValues) => ({
        ...prevValues,
        vendorName: "",
        vendorId: "",
        quoteNo: "",
        taxableValue: "",
        gst: "",
        taxAmount: "",
        shipTo: "",
        billTo: "",
        referenceNoDate: "",
        buyerOrderNo: "",
        buyerDate: "",
        termsOfPayment: "",
        vehicleRegNo: "",
        serialNo: "",
        engineNo: "",
        destination: "",
        deliveryTerms: "",
        discount: "",
      }));
    }
  }, [workOrderData]);

  const productColumns: ColDef<any>[] = [
    {
      headerName: ProformaLabel.SrNo,
      headerTooltip: ProformaLabel.SrNo,
      valueGetter: "node.rowIndex + 1",
      cellStyle: { textAlign: "center" },
      editable: false,
      sortable: false,
      filter: false,
      flex: 0.5,
      minWidth: 80,
    },
    {
      headerName: ProformaLabel.ProductName,
      headerTooltip: ProformaLabel.ProductName,
      field: ProformaKey.ProductName,
      tooltipField: ProformaKey.ProductName,
      editable: false,
      flex: 1.5,
      minWidth: 150,
    },
    {
      headerName: QuotationLabel.ServiceName,
      headerTooltip: QuotationLabel.ServiceName,
      field: QuotationKey.ServiceName,
      tooltipField: QuotationKey.ServiceName,
      editable: false,
      flex: 1.5,
      minWidth: 150,
    },
    {
      headerName: ProformaLabel.DescriptionOfGoods,
      headerTooltip: ProformaLabel.DescriptionOfGoods,
      field: ProformaKey.DescriptionOfGoods,
      tooltipField: ProformaKey.DescriptionOfGoods,
      cellEditor: "agTextCellEditor",
      editable: false,
      flex: 1.5,
      minWidth: 150,
    },
    {
      headerName: ProformaLabel.DescriptionSubHead,
      headerTooltip: ProformaLabel.DescriptionSubHead,
      field: ProformaKey.DescriptionSubHead,
      tooltipField: ProformaKey.DescriptionSubHead,
      cellEditor: "agTextCellEditor",
      editable: false,
      flex: 1.5,
      minWidth: 150,
    },
    {
      headerName: ProformaLabel.HSN_SAC,
      headerTooltip: ProformaLabel.HSN_SAC,
      field: ProformaKey.HSN_SAC,
      tooltipField: ProformaKey.HSN_SAC,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: hsnSacOption?.map((option) => option?.label),
        formatValue: (value: any) => {
          const selectedOption = hsnSacOption?.find(
            (opt) => opt?.label === value
          );
          return selectedOption ? selectedOption?.label : value;
        },
      },
      flex: 1.5,
      minWidth: 150,
    },
    {
      headerName: ProformaLabel.GST,
      headerTooltip: ProformaLabel.GST,
      field: ProformaKey.QuotationGst,
      tooltipField: ProformaKey.QuotationGst,
      cellEditor: "agSelectCellEditor",
      flex: 2,
      minWidth: 150,
    },
    {
      headerName: ProformaLabel.Quantity,
      headerTooltip: ProformaLabel.Quantity,
      field: ProformaKey.Quantity,
      tooltipField: ProformaKey.Quantity,
      flex: 2,
      minWidth: 150,
    },
    {
      headerName: ProformaLabel.Rate,
      headerTooltip: ProformaLabel.Rate,
      field: ProformaKey.Rate,
      tooltipField: ProformaKey.Rate,
      flex: 2,
      minWidth: 150,
    },
    {
      headerName: ProformaLabel.Per,
      headerTooltip: ProformaLabel.Per,
      field: ProformaKey.Per,
      tooltipField: ProformaKey.Per,
      editable: false,
      flex: 1.5,
      minWidth: 150,
    },
    {
      headerName: ProformaLabel.TotalAmount,
      headerTooltip: ProformaLabel.TotalAmount,
      field: ProformaKey.Amount,
      tooltipField: ProformaKey.Amount,
      editable: false,
      cellRenderer: (params: any) => {
        const amount = params.value?.toFixed(2) || 0;
        return rupeeSymbol(amount);
      },
      flex: 2.5,
      minWidth: 150,
    },
  ];

  const formInitialValue = {
    workOrderNo: proformaId ? proformaInvoice?.work_order_id : null,
    vendorName: "",
    vendorId: "",
    quoteNo: "",
    proformaInvoiceDT: proformaId
      ? NormalDate(proformaInvoice?.pro_in_dt)
      : currentDate,
    taxableValue: "",
    gst: "",
    taxAmount: "",
    warranty: proformaId ? proformaInvoice?.warranty : null,
    remarks: proformaId ? proformaInvoice?.remarks : null,
    shipTo: "",
    billTo: "",
    deliveryNote: proformaId ? proformaInvoice?.delivery_note : null,
    deliveryDate: proformaId
      ? NormalDate(proformaInvoice?.delivery_date)
      : currentDate,
    termsOfPayment: proformaId ? proformaInvoice?.terms_of_payment : null,
    referenceNoDate: "",
    vehicleRegNo: proformaId ? proformaInvoice?.vehicle_registration_no : null,
    serialNo: proformaId ? proformaInvoice?.chassis_no : null,
    engineNo: proformaId ? proformaInvoice?.engine_no : null,
    buyerOrderNo: "",
    buyerDate: "",
    dispatchedThrough: proformaId ? proformaInvoice?.dispatched_through : null,
    destination: proformaId ? proformaInvoice?.destination : null,
    motorVehicleNo: proformaId ? proformaInvoice?.vehicle_no : null,
    deliveryTerms: proformaId ? proformaInvoice?.terms_of_delivery : null,
    product: null,
    discount: "",
  };
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formInitialValue,
    validationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });
  const handleSubmit = (values: any) => {
    const payload = {
      quotation_id: values?.quoteNo,
      work_order_id: values?.workOrderNo,
      customer_name: values?.vendorName,
      customer_id: values?.vendorId,
      pro_in_dt: values.proformaInvoiceDT,
      taxable: Number(values.taxableValue),
      gst: Number(values.gst),
      tax_invoice_amount: Number(values.taxAmount),
      warranty: values.warranty,
      shipping_address: values.shipTo,
      billing_address: values.billTo,
      remarks: values.remarks,
      terms_of_payment: values.termsOfPayment,
      reference_no: quotationNumber,
      reference_date: values.referenceNoDate,
      vehicle_no: values.motorVehicleNo,
      vehicle_registration_no: values.vehicleRegNo,
      chassis_no: values.serialNo,
      buyer_order_no: values.buyerOrderNo,
      engine_no: values.engineNo,
      buyer_order_date: values.buyerDate,
      dispatched_through: values.dispatchedThrough,
      destination: values.destination,
      delivery_note: values.deliveryNote,
      delivery_date: values.deliveryDate,
      terms_of_delivery: values.deliveryTerms,
    };
    setSubmitLoader(true);
    if (proformaId) {
      editProformaAPi(Number(proformaId), payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            navigate("/proforma-invoice");
          } else {
            if (Array.isArray(res?.message)) {
              res?.message?.map((message: string) => {
                toast.error(message);
              });
            } else {
              toast.error(res?.response?.message || res?.message);
            }
          }
        })
        .catch((err) => {
          errorHandler(err);
        })
        .finally(() => {
          setSubmitLoader(false);
        });
    } else {
      addProformaApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            navigate("/proforma-invoice");
          } else {
            if (Array.isArray(res?.message)) {
              res?.message?.map((message: string) => {
                toast.error(message);
              });
            } else {
              toast.error(res?.response?.message || res?.message);
            }
          }
        })
        .catch((err) => {
          errorHandler(err);
        })
        .finally(() => {
          setSubmitLoader(false);
        });
    }
  };

  useEffect(() => {
    if (formik.values.workOrderNo) {
      fetchWorkOrderData(Number(formik.values.workOrderNo));
    } else {
      setWorkOrderData(null);
    }
  }, [formik.values.workOrderNo]);

  useEffect(() => {
    if (proformaId) {
      setWork(true);
    } else {
      setWork(false);
    }
  }, []);

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">
            {proformaId
              ? `${ProformaLabel.EditProformaInvoice}-${
                  proformaInvoice?.pro_in_no || ""
                }`
              : ProformaLabel.NewProformaInvoice}
          </span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-danger"
              onClick={() => navigate("/proforma-invoice")}
              children={
                <>
                  <i className="mdi mdi-arrow-left" />
                </>
              }
            />
          </div>
        </div>
        <div className="card word-wrap">
          <div className="card-body">
            <FormFields
              formik={formik}
              workOrderOptions={workOrderOption}
              quotationOption={quotationOption}
              proformaId={proformaId}
              setWork={setWork}
            />
            <div className="table-css">
              <div className="ag-theme-quartz">
                <AgGridReact
                  ref={gridRef}
                  rowData={selectedProducts}
                  columnDefs={productColumns}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                    flex: 1,
                    tooltipComponentParams: {
                      color: "blue",
                    },
                  }}
                  domLayout="normal"
                  getRowHeight={() => 35}
                  overlayNoRowsTemplate={notFound.dataNotFound}
                />
              </div>
            </div>
            <div className="row mt-3 d-flex justify-content-end">
              <div className="col-auto d-flex justify-content-end gap-2 text-end">
                <BaseButton
                  type="button"
                  label={proformaId ? updateLabel : submitLabel}
                  color="success"
                  loader={submitLoader}
                  disable={submitLoader}
                  onClick={() => formik.handleSubmit()}
                />
                <BaseButton
                  type="button"
                  label={cancelLabel}
                  onClick={() => navigate("sales-order/proforma-invoice")}
                  color="danger"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProformaInvoiceForm;
